import useApiSortingDebt from "@dues/apis/sorting-debt/useApiSortingDebt";
import { useDebtsContext } from "@dues/contexts/DebtsContext/useDebtsContext";
import useSimulatedDebt from "@dues/hooks/useSimulatedDebt";
import useAuthenticatedUser from "@libs/acp/hooks/useAuthenticatedUser";
import type { CheckoutSessionConfig } from "@libs/renegkit/pages/Checkout/PaymentPrefs/hook";
import { useUser } from "@shared/hooks/useUser";
import { pageNames } from "@shared/routes/pageNames";
import { useNavigate } from "react-router-dom";

import { JOURNEY_STORAGE_KEY } from "@dues/pages/MyDebts/hooks/ExperienceController/services/ConsultingJourney/types";
import { EC_SESSION_KEY } from "@dues/pages/MyDebts/hooks/ExperienceController/types";
import constants from "@shared/constants";

export const useCheckoutSessionConfig = (): CheckoutSessionConfig => {
	const navigate = useNavigate();
	const { document } = useUser();
	const { cleanSimulatedDebtsCache } = useSimulatedDebt();
	const { apiSortingDebtInvalidateCache } = useApiSortingDebt();
	const { authenticatedUser, tokenOrigin, resetStore } = useAuthenticatedUser();
	const { cleanDebtsContext } = useDebtsContext();
	const {
		SORTING_DEBTS_RES,
		DEBT,
		PAYMENT_OPTION,
		PAYMENT_METHOD,
		PIX_STATUS,
		COUNTDOWN_STARTED_AT,
		PROPERTIES,
	} = constants.debts;

	const removeInforFromLocalStorage = () => {
		const localStorageKeys = [
			COUNTDOWN_STARTED_AT,
			SORTING_DEBTS_RES,
			DEBT,
			PAYMENT_OPTION,
			PAYMENT_METHOD,
			PIX_STATUS,
			PROPERTIES.PAYMENT_DATE,
		];

		const sessionStorageKeys = [JOURNEY_STORAGE_KEY, EC_SESSION_KEY];

		localStorageKeys.forEach((key) => {
			if (localStorage.getItem(key)) {
				localStorage.removeItem(key);
			}
		});

		sessionStorageKeys.forEach((key) => {
			if (sessionStorage.getItem(key)) {
				sessionStorage.removeItem(key);
			}
		});
	};

	const cleanAllDebtsCache = () => {
		removeInforFromLocalStorage();
		cleanSimulatedDebtsCache();
		cleanDebtsContext();
	};

	const onExpiredSession = async (creditorSystemSlug?: string) => {
		await apiSortingDebtInvalidateCache
			.send({
				documento: document,
				provider_identifier: creditorSystemSlug ?? "",
			})
			.finally(() => {
				cleanAllDebtsCache();

				const isNCADCheckout =
					authenticatedUser?.customer?.required_updates?.includes(
						"account_ncad",
					);
				const isNCADGeneral = !isNCADCheckout && tokenOrigin() === "ncad";

				if (isNCADCheckout || isNCADGeneral) {
					sessionStorage.removeItem("temp_closedAgreementRedirect");
					resetStore();
				}

				navigate(pageNames.myDebts.path);
			});
	};

	const onAgreementClosed = (creditorSystemSlug?: string) => {
		apiSortingDebtInvalidateCache
			.send({
				documento: document,
				provider_identifier: creditorSystemSlug ?? "",
			})
			.then(() => {
				cleanAllDebtsCache();
			});
	};

	return { onExpiredSession, onAgreementClosed };
};
